import{
 Alert,Box,Button,Card,CardContent,Chip,CircularProgress,LinearProgress,
 Paper,Stack,Typography,type ButtonProps
}from'@mui/material';
import{useIsFetching,useIsMutating}from'@tanstack/react-query';
import RasanaLogo from'./RasanaLogo';
import{rasanaTokens}from'./theme';

export function PageHeader({
 title,subtitle,action
}:{title:string;subtitle?:string;action?:React.ReactNode}){
 return <Stack
  direction={{xs:'column',sm:'row'}}
  justifyContent="space-between"
  alignItems={{sm:'flex-start'}}
  spacing={2}
  mb={3}
 >
  <Box>
   <Box sx={{
    width:28,
    height:3,
    borderRadius:2,
    bgcolor:rasanaTokens.gold,
    mb:1.25
   }}/>
   <Typography variant="h4">{title}</Typography>
   {subtitle&&
    <Typography
     color="text.secondary"
     sx={{mt:.5,maxWidth:760}}
    >
     {subtitle}
    </Typography>
   }
  </Box>

  {action&&
   <Box sx={{pt:{sm:.5}}}>
    {action}
   </Box>
  }
 </Stack>;
}

export function Kpi({label,value,detail}:{
 label:string;
 value:React.ReactNode;
 detail?:React.ReactNode;
}){
 return <Card sx={{
  height:'100%',
  position:'relative',
  overflow:'hidden',
  '&::before':{
   content:'""',
   position:'absolute',
   left:0,
   top:0,
   bottom:0,
   width:3,
   bgcolor:rasanaTokens.primary
  }
 }}>
  <CardContent>
   <Typography
    color="text.secondary"
    variant="body2"
    fontWeight={500}
   >
    {label}
   </Typography>

   <Typography
    sx={{
     mt:.75,
     fontSize:{xs:26,md:30},
     lineHeight:1.15,
     fontWeight:600,
     color:rasanaTokens.navy
    }}
   >
    {value}
   </Typography>

   {detail&&
    <Typography
     variant="caption"
     color="text.secondary"
     sx={{display:'block',mt:.75}}
    >
     {detail}
    </Typography>
   }
  </CardContent>
 </Card>;
}

export function Health({value}:{value:string}){
 const color=value==='Green'
  ?'success'
  :value==='Amber'
    ?'warning'
    :value==='Red'
      ?'error'
      :'default';

 return <Chip
  label={value}
  color={color as any}
  size="small"
  variant="outlined"
 />;
}

export function ErrorBox({error}:{error:unknown}){
 return error
  ?<Alert severity="error" sx={{mb:2}}>
    {error instanceof Error?error.message:String(error)}
   </Alert>
  :null;
}

export function EmptyState({
 title='Nothing here yet',
 description='There is no data to display.'
}:{title?:string;description?:string}){
 return <Paper
  variant="outlined"
  sx={{
   p:{xs:3,md:4},
   textAlign:'center',
   bgcolor:rasanaTokens.ivory
  }}
 >
  <Box sx={{
   display:'flex',
   justifyContent:'center',
   mb:1.5,
   opacity:.72
  }}>
   <RasanaLogo variant="mark" markSize={42}/>
  </Box>

  <Typography variant="subtitle1">{title}</Typography>

  <Typography
   variant="body2"
   color="text.secondary"
   mt={.5}
  >
   {description}
  </Typography>
 </Paper>;
}

export function LoadingState({label='Loading…'}:{label?:string}){
 return <Stack
  direction="row"
  spacing={1.5}
  alignItems="center"
  py={2}
 >
  <CircularProgress size={18} thickness={4}/>
  <Typography variant="body2" color="text.secondary">
   {label}
  </Typography>
 </Stack>;
}

export function BusyButton({
 busy=false,
 busyLabel='Processing…',
 children,
 disabled,
 startIcon,
 ...props
}:ButtonProps&{busy?:boolean;busyLabel?:string}){
 return <Button
  {...props}
  disabled={Boolean(disabled)||busy}
  startIcon={
   busy
    ?<CircularProgress size={16} color="inherit"/>
    :startIcon
  }
 >
  {busy?busyLabel:children}
 </Button>;
}

export function GlobalActivity(){
 const fetching=useIsFetching();
 const mutating=useIsMutating();
 const active=fetching+mutating;

 if(!active)return null;

 return <>
  <LinearProgress sx={{
   position:'fixed',
   top:0,
   left:0,
   right:0,
   zIndex:theme=>theme.zIndex.snackbar+2,
   height:2
  }}/>

  <Paper
   elevation={0}
   sx={{
    position:'fixed',
    top:12,
    right:16,
    zIndex:theme=>theme.zIndex.snackbar+1,
    px:1.5,
    py:.8,
    pointerEvents:'none',
    border:`1px solid ${rasanaTokens.borderSoft}`,
    boxShadow:rasanaTokens.shadowSoft
   }}
  >
   <Stack direction="row" spacing={1} alignItems="center">
    <CircularProgress size={14}/>
    <Typography variant="caption">
     {mutating?'Processing changes…':'Loading data…'}
    </Typography>
   </Stack>
  </Paper>
 </>;
}
